import { DownOutlined, SearchOutlined } from '@ant-design/icons'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { Input } from 'sutra/components/Input/Input'
import { LogoMark } from 'sutra/components/Logo/Logo'
import { Popover } from 'sutra/components/Popover/Popover'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import { darkBlue, primaryOrange } from 'assets/styles/variables'
import { ProjectState } from 'types/project'

type Props = {
  isCollapsed?: boolean
  details?: ProjectState['details']
  projects?: { proj_uuid: string; title: string }[]
}

export const HubSelector: React.FC<Props> = ({
  isCollapsed = false,
  details,
  projects: projectsProp,
}) => {
  // state
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const listItemRefs = useRef<(HTMLDivElement | null)[]>([])

  const projects = useMemo(() => projectsProp ?? [], [projectsProp])

  // constants
  const selectedProject = {
    proj_uuid: details?.proj_uuid,
    title: details?.title_override ?? details?.title,
  }

  // Get unique projects - deduplicate by uuid
  const uniqueProjects = useMemo(() => {
    const seen = new Set()
    return projects.filter((project) => {
      if (seen.has(project.proj_uuid)) {
        return false
      }
      seen.add(project.proj_uuid)
      return true
    })
  }, [projects])

  // filtered projects based on search
  const filteredProjects = useMemo(() => {
    if (!searchQuery.trim()) return uniqueProjects
    const query = searchQuery.toLowerCase()
    return uniqueProjects.filter((project) =>
      project.title.toLowerCase().includes(query),
    )
  }, [uniqueProjects, searchQuery])

  // Reset refs array when filtered projects change
  useEffect(() => {
    listItemRefs.current = []
  }, [filteredProjects])

  // handlers
  const handleOpenChange = (open: boolean): void => {
    setIsOpen(open)
    if (open) {
      setSearchQuery('')
      setFocusedIndex(-1)
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 100)
    }
  }

  const handleSelectProject = (uuid: string): void => {
    // Force full page reload to completely reset all state
    // Use href instead of pathname to bypass React Router client-side navigation
    window.location.href = '/project/' + uuid
    setIsOpen(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (filteredProjects.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setFocusedIndex((prev) => {
          const newIndex = prev < filteredProjects.length - 1 ? prev + 1 : prev
          scrollToItem(newIndex)
          return newIndex
        })
        break
      case 'ArrowUp':
        e.preventDefault()
        setFocusedIndex((prev) => {
          const newIndex = prev > 0 ? prev - 1 : 0
          scrollToItem(newIndex)
          return newIndex
        })
        break
      case 'Enter':
        e.preventDefault()
        if (focusedIndex >= 0 && focusedIndex < filteredProjects.length) {
          handleSelectProject(filteredProjects[focusedIndex]!.proj_uuid)
        }
        break
      case 'Escape':
        e.preventDefault()
        setIsOpen(false)
        break
    }
  }

  const scrollToItem = (index: number): void => {
    setTimeout(() => {
      const element = listItemRefs.current[index]
      if (element) {
        element.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        })
      }
    }, 0)
  }

  if (!projects?.length || !details) {
    return <ButtonContainer />
  }

  const dropdownContent = (
    <DropdownContainer>
      <SearchWrapper>
        <StyledInput
          ref={searchInputRef}
          placeholder="Search hubs..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value)
            setFocusedIndex(-1)
          }}
          onKeyDown={handleKeyDown}
          prefix={<SearchOutlined style={{ color: '#999' }} />}
          allowClear
        />
      </SearchWrapper>

      <ProjectList>
        {filteredProjects.length === 0 ? (
          <NoResults>No hubs found</NoResults>
        ) : (
          filteredProjects.map((project, index) => (
            <ProjectItem
              key={project.proj_uuid}
              ref={(el) => (listItemRefs.current[index] = el)}
              $isActive={project.proj_uuid === selectedProject.proj_uuid}
              $isFocused={index === focusedIndex}
              onClick={() => handleSelectProject(project.proj_uuid)}
            >
              <ProjectTitle>{project.title}</ProjectTitle>
              {project.proj_uuid === selectedProject.proj_uuid && (
                <ActiveBadge>Current</ActiveBadge>
              )}
            </ProjectItem>
          ))
        )}
      </ProjectList>
    </DropdownContainer>
  )

  return (
    <Tooltip content={selectedProject.title} side="bottom">
      <span style={{ display: 'block', width: '100%' }}>
        <HubPopover
          open={isOpen}
          onOpenChange={handleOpenChange}
          content={dropdownContent}
          side="bottom"
          align="start"
          aria-label="Hubs"
        >
          <ButtonContainer>
            <IconWrapper>
              <LogoMark $tone="navy" $size={20} />
            </IconWrapper>
            {!isCollapsed && <HubTitle>{selectedProject.title}</HubTitle>}
            {!isCollapsed && (
              <DropdownIconWrapper>
                <DropdownIcon />
              </DropdownIconWrapper>
            )}
          </ButtonContainer>
        </HubPopover>
      </span>
    </Tooltip>
  )
}

// The panel brings its own box, so the Popover popup's is stripped.
const HubPopover = styled(Popover)`
  && {
    padding: 0;
    background: transparent;
    box-shadow: none;
  }
`

const ButtonContainer = styled.button`
  display: flex;
  min-height: 28px;
  align-items: center;
  padding: 6px 0 6px 6px;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  width: 100%;
  text-align: left;
  overflow: hidden;

  &:hover {
    background: #5072a426;
  }
`

const IconWrapper = styled.div`
  width: 20px;
  height: 20px;
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const DropdownIconWrapper = styled.div`
  width: 20px;
  height: 20px;
  margin-left: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const HubTitle = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${darkBlue};
  flex: 1;
  min-width: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-wrap: break-word;
  overflow-wrap: break-word;
  user-select: none;
`

const DropdownIcon = styled(DownOutlined)`
  color: ${darkBlue};
  font-size: 10px;
  flex-shrink: 0;
`

const DropdownContainer = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  width: 400px;
  max-height: 500px;
  display: flex;
  flex-direction: column;
`

const SearchWrapper = styled.div`
  padding: 6px;
  // background: #eeeff3;
  border-bottom: 1px solid #f0f0f0;
`

const StyledInput = styled(Input)`
  font-size: 13px;
  // padding: 10px 12px;
  border-radius: 8px;

  &:focus-within,
  &:hover {
    border-color: ${primaryOrange};
  }
`

const ProjectList = styled.div`
  max-height: 400px;
  overflow-y: auto;
`

const ProjectItem = styled.div<{ $isActive: boolean; $isFocused: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 12px;
  border-bottom: 1px solid #eeeff3;
  cursor: pointer;
  transition: background 0.2s ease;
  background: ${({ $isActive, $isFocused }) =>
    $isFocused ? '#5072a426' : $isActive ? '#e9e9e9' : 'transparent'};
  border-left: 3px solid
    ${({ $isActive }) => ($isActive ? primaryOrange : 'transparent')};

  &:hover {
    background: #5072a426;
  }
`

const ProjectTitle = styled.span`
  font-size: 13px;
  font-weight: 400;
  color: ${darkBlue};
  flex: 1;
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: 1.4;
`

const ActiveBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${primaryOrange};
  background: #fff7e6;
  padding: 4px 12px;
  border-radius: 12px;
  margin-left: 12px;
  flex-shrink: 0;
`

const NoResults = styled.div`
  padding: 40px 24px;
  text-align: center;
  color: #999;
  font-size: 14px;
`
